import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Users, Bot, Plus, Coins, HelpCircle, Spade } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClickableAvatar } from '../../components/UserStatsModal';

const DefaultAvatar = ({ size = 48 }) => (
  <div 
    className="rounded-full bg-slate-700 flex items-center justify-center"
    style={{ width: size, height: size }}
  >
    <Users size={size * 0.5} className="text-slate-500" />
  </div>
);

// House Bot Avatar
const HouseAvatar = ({ size = 64 }) => (
  <div 
    className="rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center border-2 border-slate-600"
    style={{ width: size, height: size }}
  >
    <Bot size={size * 0.5} className="text-purple-400" />
  </div>
);

// Result Display Component with 1-minute timer
const GameResultDisplay = ({ game, user, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const playerWon = ['win', 'dealer_bust', 'blackjack'].includes(game.result);
  const isPush = game.result === 'push';
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass rounded-2xl p-8"
    >
      <h2 className="text-2xl font-bold text-white mb-6 text-center">Game Over</h2>
      
      {/* Players with Avatars */}
      <div className="flex items-center justify-between mb-8">
        {/* Player */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative ${playerWon ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : isPush ? '' : 'opacity-50 grayscale'}`}
            animate={playerWon ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.5, repeat: playerWon ? 2 : 0 }}
          >
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-purple-500">
              {user?.roblox_avatar_url ? (
                <img src={user.roblox_avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <DefaultAvatar size={64} />
              )}
            </div>
            {playerWon && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-bold text-sm ${playerWon ? 'text-blue-400' : isPush ? 'text-yellow-400' : 'text-slate-400'}`}>
            {user?.username || 'You'}
          </p>
          <p className="text-slate-500 text-xs">({game.your_value})</p>
        </div>

        {/* VS */}
        <div className="flex flex-col items-center">
          <span className="text-slate-400 font-bold text-xl">VS</span>
        </div>

        {/* Dealer/House */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative ${!playerWon && !isPush ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : isPush ? '' : 'opacity-50 grayscale'}`}
            animate={!playerWon && !isPush ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.5, repeat: !playerWon && !isPush ? 2 : 0 }}
          >
            <HouseAvatar size={64} />
            {!playerWon && !isPush && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -left-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-bold text-sm ${!playerWon && !isPush ? 'text-blue-400' : isPush ? 'text-yellow-400' : 'text-slate-400'}`}>
            House
          </p>
          <p className="text-slate-500 text-xs">({game.dealer_value})</p>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 gap-8 mb-6">
        {/* Player Cards */}
        <div>
          <h3 className="text-slate-400 mb-3 text-center text-sm">Your Hand</h3>
          <div className="flex justify-center gap-1 flex-wrap">
            {game.your_cards?.map((card, i) => <Card key={i} card={card} />)}
          </div>
        </div>
        
        {/* Dealer Cards */}
        <div>
          <h3 className="text-slate-400 mb-3 text-center text-sm">Dealer Hand</h3>
          <div className="flex justify-center gap-1 flex-wrap">
            {game.dealer_cards?.map((card, i) => <Card key={i} card={card} />)}
          </div>
        </div>
      </div>
      
      {/* Result */}
      <div className={`text-center p-6 rounded-xl mb-6 ${
        playerWon 
          ? 'bg-blue-500/20 border border-blue-500/30' 
          : isPush 
            ? 'bg-yellow-500/20 border border-yellow-500/30'
            : 'bg-red-500/20 border border-red-500/30'
      }`}>
        <p className={`text-2xl font-bold ${
          playerWon 
            ? 'text-blue-400' 
            : isPush 
              ? 'text-yellow-400'
              : 'text-red-400'
        }`}>
          {game.result === 'win' && `You Won R$${game.payout?.toFixed(0)}!`}
          {game.result === 'dealer_bust' && `Dealer Busts! Won R$${game.payout?.toFixed(0)}!`}
          {game.result === 'blackjack' && `Blackjack! Won R$${game.payout?.toFixed(0)}!`}
          {game.result === 'bust' && 'Bust! You Lost'}
          {game.result === 'lose' && 'House Wins'}
          {game.result === 'push' && `Push - R$${game.payout?.toFixed(0)} returned`}
        </p>
      </div>
      
      <Button onClick={onClose} className="w-full btn-primary">
        Play Again
      </Button>
    </motion.div>
  );
};

const Card = ({ card, hidden = false }) => {
  if (hidden) {
    return (
      <div className="w-14 h-20 rounded-lg bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-700 flex items-center justify-center shadow-lg">
        <span className="text-xl">🂠</span>
      </div>
    );
  }

  const isRed = ['hearts', 'diamonds'].includes(card.suit);
  const suitSymbol = {
    hearts: '♥',
    diamonds: '♦',
    clubs: '♣',
    spades: '♠'
  }[card.suit];

  return (
    <div className={`w-14 h-20 rounded-lg bg-white border-2 border-slate-300 flex flex-col items-center justify-center shadow-lg ${isRed ? 'text-red-600' : 'text-slate-900'}`}>
      <span className="text-base font-bold">{card.rank}</span>
      <span className="text-lg">{suitSymbol}</span>
    </div>
  );
};

// P2P Game Card - Matching the reference design
const P2PGameCard = ({ game, currentUser, onJoin, onCancel, loading }) => {
  const isCreator = currentUser?.user_id === game.creator_id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 rounded-2xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all"
    >
      <div className="flex items-center justify-between">
        {/* Player 1 (Creator) */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <ClickableAvatar
              userId={game.creator_id}
              username={game.creator_username}
              avatarUrl={game.creator_avatar}
              size={64}
              borderColor="border-green-500"
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
              <Spade size={14} className="text-white" />
            </div>
          </div>
          <div className="hidden sm:block">
            <p className="text-white font-medium text-sm truncate max-w-[80px]">{game.creator_username}</p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
        </div>

        {/* Center - VS and Prize */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-slate-400 font-bold text-lg">VS</span>
          <span className="text-purple-400 font-bold text-lg">R${(game.amount * 2).toLocaleString()}</span>
        </div>

        {/* Player 2 (Opponent or Waiting) */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <p className="text-slate-400 font-medium text-sm">Waiting...</p>
            <p className="text-slate-500 text-xs">R${game.amount.toLocaleString()}</p>
          </div>
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-dashed border-purple-500/50 flex items-center justify-center bg-slate-800/50">
              <HelpCircle className="text-slate-600" size={32} />
            </div>
            <div className="absolute -bottom-1 -left-1 w-6 h-6 bg-purple-600/50 rounded-full flex items-center justify-center">
              <Spade size={14} className="text-white/50" />
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-4 flex justify-center">
        {isCreator ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCancel(game.game_id)}
            className="text-red-400 border-red-500/50 hover:bg-red-500/10"
          >
            Cancel Game
          </Button>
        ) : (
          <Button
            onClick={() => onJoin(game.game_id, game.amount)}
            disabled={loading}
            className="btn-primary px-6"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Join R${game.amount.toLocaleString()}</>}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// P2P Completed Game Result Display - Shows for 1 minute
const P2PCompletedGameCard = ({ game, currentUser }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const winnerId = game.winner_id;
  const isCreatorWinner = winnerId === game.creator_id;
  const isCurrentUserWinner = winnerId === currentUser?.user_id;

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-r from-slate-800/90 to-slate-900/90 rounded-2xl p-4 border border-purple-500/50"
    >
      <div className="flex items-center justify-between mb-4">
        {/* Creator */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative ${isCreatorWinner ? 'ring-4 ring-blue-400 ring-opacity-70 rounded-full' : 'opacity-40 grayscale'}`}
          >
            <ClickableAvatar
              userId={game.creator_id}
              username={game.creator_username}
              avatarUrl={game.creator_avatar}
              size={56}
              borderColor="border-purple-500"
            />
            {isCreatorWinner && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-medium text-xs ${isCreatorWinner ? 'text-blue-400' : 'text-slate-400'}`}>
            {game.creator_username}
          </p>
          <p className="text-slate-500 text-xs">({game.opponent_value})</p>
        </div>

        {/* Center - Cards & Result */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex gap-4">
            {/* Creator cards */}
            <div className="flex gap-1">
              {game.opponent_cards?.map((card, i) => <Card key={i} card={card} />)}
            </div>
            <span className="text-slate-400 font-bold self-center">VS</span>
            {/* Joiner cards */}
            <div className="flex gap-1">
              {game.your_cards?.map((card, i) => <Card key={i} card={card} />)}
            </div>
          </div>
          <span className="text-purple-400 font-bold">R${(game.amount * 2).toLocaleString()}</span>
        </div>

        {/* Joiner (current user) */}
        <div className="flex flex-col items-center gap-2">
          <motion.div 
            className={`relative w-14 h-14 rounded-full overflow-hidden border-2 border-purple-500 ${!isCreatorWinner ? 'ring-4 ring-blue-400 ring-opacity-70' : 'opacity-40 grayscale'}`}
          >
            {currentUser?.roblox_avatar_url ? (
              <img src={currentUser.roblox_avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <DefaultAvatar size={56} />
            )}
            {!isCreatorWinner && (
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -left-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
              >
                ✓
              </motion.div>
            )}
          </motion.div>
          <p className={`font-medium text-xs ${!isCreatorWinner ? 'text-blue-400' : 'text-slate-400'}`}>
            {currentUser?.username || 'You'}
          </p>
          <p className="text-slate-500 text-xs">({game.your_value})</p>
        </div>
      </div>

      {/* Result Message */}
      <div className="text-center">
        <p className={`text-lg font-bold ${isCurrentUserWinner ? 'text-blue-400' : 'text-red-400'}`}>
          {isCurrentUserWinner ? `You won R$${(game.amount * 2).toLocaleString()}!` : 'You lost!'}
        </p>
      </div>
    </motion.div>
  );
};

export default function Blackjack() {
  const { user, api, refreshUser, refreshRobloxProfile } = useAuth();
  const [mode, setMode] = useState('p2p');
  const [betAmount, setBetAmount] = useState('100');
  const [loading, setLoading] = useState(false);
  
  // Set page title
  useEffect(() => {
    document.title = 'RBX.Casino | Blackjack';
  }, []);
  
  // Game state - persist active game in localStorage
  const [currentGame, setCurrentGame] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('blackjack_current_game');
      return saved ? JSON.parse(saved) : null;
    }
    return null;
  });
  const [activeP2PGames, setActiveP2PGames] = useState([]);
  const [completedP2PGames, setCompletedP2PGames] = useState([]); // Store completed games for 1 min display

  // Synchronous gate so each completed game is added to "Recent Results" exactly once,
  // even across racing pollers / after being shown as the full-screen result.
  const processedCompletedRef = useRef(new Set());

  const addCompletedGame = (game) => {
    if (!game?.game_id) return;
    if (processedCompletedRef.current.has(game.game_id)) return;
    processedCompletedRef.current.add(game.game_id);
    setCompletedP2PGames(prev => [...prev, { ...game, completedAt: Date.now() }]);
    setTimeout(() => {
      setCompletedP2PGames(p => p.filter(g => g.game_id !== game.game_id));
    }, 60000);
  };
  
  // Track creator's waiting game - persist in localStorage
  const [myPendingGameId, setMyPendingGameId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('blackjack_pending_game') || null;
    }
    return null;
  });
  
  // Sync pending game to localStorage
  useEffect(() => {
    if (myPendingGameId) {
      localStorage.setItem('blackjack_pending_game', myPendingGameId);
    } else {
      localStorage.removeItem('blackjack_pending_game');
    }
  }, [myPendingGameId]);

  // Sync current game to localStorage
  useEffect(() => {
    if (currentGame && currentGame.mode === 'p2p' && currentGame.status === 'playing') {
      localStorage.setItem('blackjack_current_game', JSON.stringify(currentGame));
    } else {
      localStorage.removeItem('blackjack_current_game');
    }
  }, [currentGame]);

  // Validate persisted game on load - fetch latest state from server
  useEffect(() => {
    const validatePersistedGame = async () => {
      if (currentGame && currentGame.game_id && currentGame.mode === 'p2p') {
        try {
          const res = await api.get(`/games/blackjack/${currentGame.game_id}`);
          if (res.data.game) {
            const gameData = res.data.game;
            if (gameData.status === 'completed') {
              // Game ended while offline - clear it
              setCurrentGame(null);
              localStorage.removeItem('blackjack_current_game');
            } else if (gameData.status === 'playing') {
              // Update with fresh data
              setCurrentGame(prev => ({
                ...prev,
                your_cards: gameData.your_cards,
                your_value: gameData.your_value,
                opponent_card_count: gameData.opponent_card_count,
                current_turn: gameData.current_turn,
                is_your_turn: gameData.is_your_turn,
                status: gameData.status
              }));
            }
          }
        } catch (error) {
          // Game doesn't exist anymore - clear it
          setCurrentGame(null);
          localStorage.removeItem('blackjack_current_game');
        }
      }
    };
    validatePersistedGame();
  }, []);
  
  // P2P lobby
  const [showCreate, setShowCreate] = useState(false);

  // Fetch active P2P games
  const fetchP2PGames = async () => {
    try {
      const res = await api.get('/games/blackjack/p2p/active');
      // Filter out games that are in completedP2PGames
      const completedIds = completedP2PGames.map(g => g.game_id);
      const newActiveGames = (res.data.games || []).filter(g => !completedIds.includes(g.game_id));
      setActiveP2PGames(newActiveGames);
    } catch (error) {
      console.error('Failed to fetch P2P games');
    }
  };

  // Fetch recent completed games for this user
  const fetchRecentCompleted = async () => {
    if (!user) return;
    try {
      const res = await api.get('/games/blackjack/p2p/recent/completed');
      const recentGames = res.data.games || [];
      recentGames.forEach(addCompletedGame);
    } catch (error) {
      console.error('Failed to fetch recent completed games');
    }
  };

  useEffect(() => {
    fetchP2PGames();
    fetchRecentCompleted();
    // Refresh Roblox avatar on page load
    if (user?.roblox_user_id) {
      refreshRobloxProfile();
    }
    const interval = setInterval(() => {
      fetchP2PGames();
      fetchRecentCompleted();
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for creator's pending game - check if someone joined
  useEffect(() => {
    if (myPendingGameId && !currentGame) {
      const interval = setInterval(async () => {
        try {
          const res = await api.get(`/games/blackjack/${myPendingGameId}`);
          if (res.data.game && res.data.game.status === 'playing') {
            // Someone joined! Show gameplay UI for creator
            const gameData = res.data.game;
            setCurrentGame({
              game_id: myPendingGameId,
              mode: 'p2p',
              your_cards: gameData.your_cards,
              your_value: gameData.your_value,
              opponent_card_count: gameData.opponent_card_count,
              opponent_username: gameData.opponent_username,
              opponent_avatar: gameData.opponent_avatar,
              current_turn: gameData.current_turn,
              is_your_turn: gameData.is_your_turn,
              status: 'playing',
              amount: gameData.amount
            });
            setMyPendingGameId(null);
            toast.success(`${gameData.opponent_username} joined! ${gameData.is_your_turn ? 'Your turn!' : 'Waiting for opponent...'}`);
          }
        } catch (error) {
          // Game might have been cancelled or doesn't exist
          if (error.response?.status === 404) {
            setMyPendingGameId(null);
          }
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [myPendingGameId, currentGame]);

  // Calculate stats
  const totalValue = activeP2PGames.reduce((sum, g) => sum + (g.amount * 2), 0);
  const totalGames = activeP2PGames.length;

  // Poll for game updates if in a P2P game
  useEffect(() => {
    if (currentGame && currentGame.mode === 'p2p' && currentGame.status === 'playing') {
      const interval = setInterval(async () => {
        try {
          const res = await api.get(`/games/blackjack/${currentGame.game_id}`);
          if (res.data.game) {
            const gameData = res.data.game;
            
            // If game completed, add to completedP2PGames and show result
            if (gameData.status === 'completed' && currentGame.status !== 'completed') {
              const completedGame = {
                game_id: currentGame.game_id,
                amount: currentGame.amount,
                creator_id: gameData.opponent_username === currentGame.opponent_username ? null : gameData.opponent_id,
                creator_username: currentGame.opponent_username,
                creator_avatar: currentGame.opponent_avatar,
                winner_id: gameData.winner_id,
                winner_username: gameData.winner_username,
                your_cards: gameData.your_cards,
                your_value: gameData.your_value,
                opponent_cards: gameData.opponent_cards,
                opponent_value: gameData.opponent_value,
                result: gameData.result,
                payout: gameData.payout
              };

              addCompletedGame(completedGame);
              setCurrentGame(null); // Clear current game

              refreshUser();
              
              // Show appropriate message based on result
              if (gameData.result === 'tie') {
                toast.info('It\'s a Tie! Pot split.');
              } else if (gameData.winner_id === user?.user_id) {
                toast.success(`You Won R$${gameData.payout?.toFixed(0)}!`);
              } else {
                toast.error(`${gameData.winner_username} Wins!`);
              }
              
              clearInterval(interval);
              return;
            }
            
            setCurrentGame(prev => ({
              ...prev,
              your_cards: gameData.your_cards,
              your_value: gameData.your_value,
              opponent_cards: gameData.opponent_cards,
              opponent_value: gameData.opponent_value,
              opponent_card_count: gameData.opponent_card_count,
              current_turn: gameData.current_turn,
              is_your_turn: gameData.is_your_turn,
              status: gameData.status,
              winner_id: gameData.winner_id,
              winner_username: gameData.winner_username,
              result: gameData.result,
              payout: gameData.payout
            }));
          }
        } catch (error) {
          console.error('Failed to fetch game state');
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [currentGame?.game_id, currentGame?.status, currentGame?.mode]);

  const handleCreateGame = async () => {
    if (!user) {
      toast.error('Please login to play');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum bet is R$10');
      return;
    }
    if (amount > user.site_balance) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/games/blackjack/create', { amount, mode: mode === 'p2p' ? 'p2p' : 'house' });
      
      if (mode === 'house') {
        setCurrentGame({
          game_id: res.data.game_id,
          mode: 'house',
          your_cards: res.data.player_cards,
          your_value: res.data.player_value,
          dealer_showing: res.data.dealer_showing,
          dealer_cards: res.data.dealer_cards,  // Include for blackjack
          dealer_value: res.data.dealer_value,  // Include for blackjack
          status: res.data.status,
          result: res.data.result,  // Include result for blackjack
          amount,
          payout: res.data.payout
        });
        
        if (res.data.status === 'completed') {
          toast.success(`Blackjack! Won R$${res.data.payout.toFixed(0)}!`);
        }
      } else {
        // P2P mode - store pending game ID for polling
        setMyPendingGameId(res.data.game_id);
        toast.success('Game created! Waiting for opponent...');
        setShowCreate(false);
        fetchP2PGames();
      }
      
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinP2P = async (gameId, amount) => {
    if (!user) {
      toast.error('Please login');
      return;
    }
    if (amount > user.site_balance) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/games/blackjack/join', { game_id: gameId });
      refreshUser();
      
      // Find the game in activeP2PGames to get creator info
      const game = activeP2PGames.find(g => g.game_id === gameId);
      
      if (res.data.status === 'playing') {
        // Game started - show P2P gameplay UI
        setCurrentGame({
          game_id: gameId,
          mode: 'p2p',
          your_cards: res.data.your_cards,
          your_value: res.data.your_value,
          opponent_card_count: res.data.opponent_card_count,
          opponent_username: res.data.opponent_username,
          opponent_avatar: res.data.opponent_avatar,
          current_turn: res.data.current_turn,
          is_your_turn: res.data.is_your_turn,
          status: 'playing',
          amount: res.data.amount
        });
        // Remove from active games list
        setActiveP2PGames(prev => prev.filter(g => g.game_id !== gameId));
        toast.success('Game started! Your turn to play.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    } finally {
      setLoading(false);
    }
  };

  const handleP2PAction = async (action) => {
    if (!currentGame || currentGame.mode !== 'p2p') return;
    
    setLoading(true);
    try {
      const res = await api.post('/games/blackjack/action', {
        game_id: currentGame.game_id,
        action
      });
      
      if (res.data.status === 'completed') {
        // Game ended - show result. Mark as processed so it isn't duplicated in "Recent Results".
        processedCompletedRef.current.add(currentGame.game_id);
        setCurrentGame(prev => ({
          ...prev,
          your_cards: res.data.your_cards,
          your_value: res.data.your_value,
          opponent_cards: res.data.opponent_cards,
          opponent_value: res.data.opponent_value,
          current_turn: res.data.current_turn,
          is_your_turn: false,
          status: 'completed',
          winner_id: res.data.winner_id,
          winner_username: res.data.winner_username,
          result: res.data.result,
          payout: res.data.payout
        }));
        
        refreshUser();
        if (res.data.winner_id === user?.user_id) {
          toast.success(`You Won R$${res.data.payout?.toFixed(0)}!`);
        } else if (res.data.result === 'tie') {
          toast.info('Tie! Pot split.');
        } else {
          toast.error(`${res.data.winner_username} Wins!`);
        }
      } else {
        setCurrentGame(prev => ({
          ...prev,
          your_cards: res.data.your_cards,
          your_value: res.data.your_value,
          opponent_card_count: res.data.opponent_card_count,
          current_turn: res.data.current_turn,
          is_your_turn: res.data.current_turn === user?.user_id,
          status: res.data.status
        }));
        
        if (res.data.current_turn !== user?.user_id) {
          toast.info("Waiting for opponent's turn...");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!currentGame) return;
    
    setLoading(true);
    try {
      const res = await api.post('/games/blackjack/action', {
        game_id: currentGame.game_id,
        action
      });
      
      setCurrentGame(prev => ({
        ...prev,
        your_cards: res.data.player_cards,
        your_value: res.data.player_value,
        dealer_cards: res.data.dealer_cards,
        dealer_value: res.data.dealer_value,
        status: res.data.status,
        result: res.data.result,
        payout: res.data.payout
      }));
      
      if (res.data.status === 'completed') {
        refreshUser();
        if (res.data.result === 'bust') {
          toast.error('Bust! You lost.');
        } else if (res.data.result === 'win' || res.data.result === 'dealer_bust') {
          toast.success(`You won R$${res.data.payout.toFixed(0)}!`);
        } else if (res.data.result === 'lose') {
          toast.error('Dealer wins!');
        } else if (res.data.result === 'push') {
          toast.info('Push - bet returned');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGame = async (gameId) => {
    try {
      await api.post(`/games/blackjack/cancel/${gameId}`);
      toast.success('Game cancelled');
      refreshUser();
      fetchP2PGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const resetGame = () => {
    setCurrentGame(null);
    localStorage.removeItem('blackjack_current_game');
  };

  // P2P active game view
  if (currentGame && currentGame.mode === 'p2p' && currentGame.status === 'playing') {
    const isYourTurn = currentGame.current_turn === user?.user_id;
    
    return (
      <div className="max-w-4xl mx-auto">
        <div className="glass rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">P2P Blackjack</h2>
          <p className="text-center text-purple-400 mb-6">Prize Pool: R${(currentGame.amount * 2).toLocaleString()}</p>
          
          {/* Opponent Section */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <ClickableAvatar
                userId={null}
                username={currentGame.opponent_username}
                avatarUrl={currentGame.opponent_avatar}
                size={40}
                borderColor="border-purple-500"
              />
              <h3 className="text-slate-400">{currentGame.opponent_username}&apos;s Hand</h3>
            </div>
            <div className="flex justify-center gap-2">
              {currentGame.opponent_cards ? (
                currentGame.opponent_cards.map((card, i) => <Card key={i} card={card} />)
              ) : (
                // Show face-down cards based on count
                [...Array(currentGame.opponent_card_count || 2)].map((_, i) => <Card key={i} hidden />)
              )}
            </div>
            {currentGame.opponent_value && (
              <p className="text-center text-slate-400 mt-2">({currentGame.opponent_value})</p>
            )}
          </div>
          
          {/* Your Section */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <ClickableAvatar
                userId={user?.user_id}
                username={user?.username}
                avatarUrl={user?.roblox_avatar_url}
                size={40}
                borderColor="border-green-500"
              />
              <h3 className="text-white font-medium">Your Hand ({currentGame.your_value})</h3>
            </div>
            <div className="flex justify-center gap-2">
              {currentGame.your_cards?.map((card, i) => <Card key={i} card={card} />)}
            </div>
          </div>
          
          {/* Turn Indicator & Actions */}
          {isYourTurn ? (
            <>
              <p className="text-center text-green-400 mb-4 font-medium">Your Turn!</p>
              <div className="flex justify-center gap-4">
                <Button
                  onClick={() => handleP2PAction('hit')}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-500 px-8"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Hit'}
                </Button>
                <Button
                  onClick={() => handleP2PAction('stand')}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-500 px-8"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Stand'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center">
              <p className="text-yellow-400 mb-4 font-medium">Waiting for {currentGame.opponent_username}...</p>
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-yellow-400" />
            </div>
          )}
          
          <p className="text-center text-slate-500 mt-4">Bet: R${currentGame.amount}</p>
        </div>
      </div>
    );
  }

  // P2P completed game view
  if (currentGame && currentGame.mode === 'p2p' && currentGame.status === 'completed') {
    const youWon = currentGame.winner_id === user?.user_id;
    const isTie = currentGame.result === 'tie';
    
    return (
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-2xl p-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Game Over</h2>
          
          {/* Both players' hands */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            {/* Opponent */}
            <div className={`p-4 rounded-xl ${!youWon && !isTie ? 'bg-blue-500/20 border border-blue-500/50' : 'opacity-60'}`}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <ClickableAvatar
                  userId={null}
                  username={currentGame.opponent_username}
                  avatarUrl={currentGame.opponent_avatar}
                  size={32}
                  borderColor="border-purple-500"
                />
                <h3 className={`text-sm font-medium ${!youWon && !isTie ? 'text-blue-400' : 'text-slate-400'}`}>
                  {currentGame.opponent_username}
                </h3>
                {!youWon && !isTie && <span className="text-blue-400">✓</span>}
              </div>
              <div className="flex justify-center gap-1 flex-wrap">
                {currentGame.opponent_cards?.map((card, i) => <Card key={i} card={card} />)}
              </div>
              <p className="text-center text-slate-400 mt-2">({currentGame.opponent_value})</p>
            </div>
            
            {/* You */}
            <div className={`p-4 rounded-xl ${youWon && !isTie ? 'bg-blue-500/20 border border-blue-500/50' : 'opacity-60'}`}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <ClickableAvatar
                  userId={user?.user_id}
                  username={user?.username}
                  avatarUrl={user?.roblox_avatar_url}
                  size={32}
                  borderColor="border-green-500"
                />
                <h3 className={`text-sm font-medium ${youWon && !isTie ? 'text-blue-400' : 'text-slate-400'}`}>
                  You
                </h3>
                {youWon && !isTie && <span className="text-blue-400">✓</span>}
              </div>
              <div className="flex justify-center gap-1 flex-wrap">
                {currentGame.your_cards?.map((card, i) => <Card key={i} card={card} />)}
              </div>
              <p className="text-center text-slate-400 mt-2">({currentGame.your_value})</p>
            </div>
          </div>
          
          {/* Result */}
          <div className={`text-center p-6 rounded-xl mb-6 ${
            youWon ? 'bg-blue-500/20 border border-blue-500/30' 
            : isTie ? 'bg-yellow-500/20 border border-yellow-500/30'
            : 'bg-red-500/20 border border-red-500/30'
          }`}>
            <p className={`text-2xl font-bold ${
              youWon ? 'text-blue-400' : isTie ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {youWon ? `You Won R$${currentGame.payout?.toFixed(0)}!` 
               : isTie ? 'Tie! Pot Split' 
               : `${currentGame.winner_username} Wins!`}
            </p>
          </div>
          
          <Button onClick={resetGame} className="w-full btn-primary">
            Play Again
          </Button>
        </motion.div>
      </div>
    );
  }

  // House active game view
  if (currentGame && currentGame.mode === 'house' && currentGame.status !== 'completed') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="glass rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Blackjack vs House</h2>
          
          {/* Dealer Section */}
          <div className="mb-8">
            <h3 className="text-slate-400 mb-3 text-center">Dealer {currentGame.dealer_value ? `(${currentGame.dealer_value})` : ''}</h3>
            <div className="flex justify-center gap-2">
              {currentGame.dealer_cards ? (
                currentGame.dealer_cards.map((card, i) => <Card key={i} card={card} />)
              ) : (
                <>
                  <Card card={currentGame.dealer_showing} />
                  <Card hidden />
                </>
              )}
            </div>
          </div>
          
          {/* Your Section */}
          <div className="mb-8">
            <h3 className="text-slate-400 mb-3 text-center">Your Hand ({currentGame.your_value})</h3>
            <div className="flex justify-center gap-2">
              {currentGame.your_cards?.map((card, i) => <Card key={i} card={card} />)}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => handleAction('hit')}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-500 px-8"
            >
              Hit
            </Button>
            <Button
              onClick={() => handleAction('stand')}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-500 px-8"
            >
              Stand
            </Button>
          </div>
          
          <p className="text-center text-slate-500 mt-4">Bet: R${currentGame.amount}</p>
        </div>
      </div>
    );
  }

  // Game result view - shows for 1 minute with avatars
  if (currentGame && currentGame.status === 'completed') {
    return (
      <div className="max-w-4xl mx-auto">
        <GameResultDisplay 
          game={currentGame} 
          user={user} 
          onClose={resetGame}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <h1 className="font-display text-3xl font-bold text-white">Blackjack</h1>

      {/* Mode Tabs */}
      <div className="flex gap-2 p-1 bg-slate-800/50 rounded-xl w-fit">
        <button
          onClick={() => setMode('p2p')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            mode === 'p2p'
              ? 'bg-purple-600 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          <Users size={18} />
          P2P
        </button>
        <button
          onClick={() => setMode('house')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            mode === 'house'
              ? 'bg-purple-600 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          <Bot size={18} />
          vs House
        </button>
      </div>

      {mode === 'p2p' ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <Coins className="text-purple-200" size={28} />
                <div>
                  <p className="text-3xl font-bold text-white">{totalValue.toLocaleString()}</p>
                  <p className="text-purple-200 text-sm">Total Value</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl p-5">
              <div className="flex items-center gap-3">
                <Users className="text-pink-200" size={28} />
                <div>
                  <p className="text-3xl font-bold text-white">{totalGames}</p>
                  <p className="text-pink-200 text-sm">Active Games</p>
                </div>
              </div>
            </div>
          </div>

          {/* Create Game Button */}
          <div className="flex justify-center">
            <Button
              onClick={() => setShowCreate(!showCreate)}
              className="bg-purple-600 hover:bg-purple-500 gap-2 px-6"
            >
              <Plus size={18} />
              Create Game
            </Button>
          </div>

          {/* Create Game Form */}
          <AnimatePresence>
            {showCreate && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="glass rounded-2xl p-6 space-y-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Bet Amount (R$)</Label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(e.target.value)}
                      min="10"
                      className="input-default"
                      placeholder="100"
                    />
                  </div>
                  <p className="text-slate-400 text-sm">
                    Simple P2P: Both players draw cards, closest to 21 without busting wins!
                  </p>
                  <Button onClick={handleCreateGame} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-500">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create Game for R$${betAmount || 0}`}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Your Pending Game - shown to creator waiting for opponent */}
          {myPendingGameId && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-yellow-400">Your Game</h2>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-4 border border-yellow-500/30"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClickableAvatar
                      userId={user?.user_id}
                      username={user?.username}
                      avatarUrl={user?.roblox_avatar_url}
                      size={48}
                      borderColor="border-yellow-500"
                    />
                    <div>
                      <p className="text-white font-medium">{user?.username}</p>
                      <p className="text-yellow-400 text-sm">Waiting for opponent...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-slate-400 text-sm">Prize Pool</p>
                      <p className="text-yellow-400 font-bold">R${(parseFloat(betAmount) * 2).toLocaleString()}</p>
                    </div>
                    <Loader2 className="h-5 w-5 animate-spin text-yellow-400" />
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Completed Games Results - Show for 1 minute */}
          {completedP2PGames.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-purple-400">Recent Results</h2>
              <AnimatePresence>
                {completedP2PGames.map((game) => (
                  <P2PCompletedGameCard
                    key={game.game_id}
                    game={game}
                    currentUser={user}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Active Games List */}
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">Active Games</h2>
            
            {activeP2PGames.length === 0 && completedP2PGames.length === 0 ? (
              <div className="text-center py-12 glass rounded-2xl">
                <Users className="mx-auto text-slate-600 mb-4" size={48} />
                <p className="text-slate-400">No active games</p>
                <p className="text-slate-500 text-sm">Create one to start playing!</p>
              </div>
            ) : activeP2PGames.length === 0 ? null : (
              <AnimatePresence>
                {activeP2PGames.map((game) => (
                  <P2PGameCard
                    key={game.game_id}
                    game={game}
                    currentUser={user}
                    onJoin={handleJoinP2P}
                    onCancel={handleCancelGame}
                    loading={loading}
                  />
                ))}
              </AnimatePresence>
            )}
          </div>
        </>
      ) : (
        /* House Mode */
        <div className="glass rounded-2xl p-8">
          <div className="space-y-6">
            <div>
              <Label className="text-slate-300 mb-2 block">Bet Amount (R$)</Label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                min="10"
                className="input-default"
                placeholder="100"
              />
              <p className="text-slate-500 text-sm mt-1">Blackjack pays 2.5x, Win pays 2x</p>
            </div>
            
            <Button onClick={handleCreateGame} disabled={loading} className="w-full btn-primary h-12 text-lg">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : `Deal Cards for R$${betAmount || 0}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
